import React from 'react';
import { Button, Form, InputNumber } from 'antd';
import { UserActionSummary } from '@/components/UserActionSummary';
import { Button as AppButton } from '@/components/Button';
import styles from './AddCreditsForm.module.scss';

const QUICK_AMOUNTS = [50, 100, 250, 500];

interface AddCreditsFormValues {
  amount: number;
}

interface AddCreditsFormProps {
  userLabel: string;
  currentBalance: number;
  onSubmit: (values: AddCreditsFormValues) => Promise<void>;
  onCancel: () => void;
}

export const AddCreditsForm: React.FunctionComponent<AddCreditsFormProps> = ({
  userLabel,
  currentBalance,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm<AddCreditsFormValues>();
  const amount = Form.useWatch('amount', form);

  const handleFinish = async (values: AddCreditsFormValues) => {
    await onSubmit(values);
    form.resetFields();
  };

  return (
    <div>
      <p className={styles.description}>
        Add extra credits directly to this user&apos;s balance, outside of any package purchase.
      </p>

      <UserActionSummary label="Crediting" userLabel={userLabel} currentBalance={currentBalance} />

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="amount"
          label="Amount to add"
          rules={[{ required: true, message: 'Enter an amount' }]}
        >
          <InputNumber
            min={1}
            size="large"
            style={{ width: '100%' }}
            placeholder="0"
          />
        </Form.Item>

        <div className={styles.quickAmounts}>
          {QUICK_AMOUNTS.map((quickAmount) => (
            <Button
              key={quickAmount}
              size="small"
              shape="round"
              className={styles.quickAmountBtn}
              onClick={() => form.setFieldValue('amount', quickAmount)}
            >
              +{quickAmount}
            </Button>
          ))}
        </div>

        <Form.Item className={styles.actionsFormItem}>
          <div className={styles.actions}>
            <AppButton appearance="outline" onClick={onCancel}>
              Cancel
            </AppButton>
            <AppButton appearance="solid" htmlType="submit" disabled={!amount}>
              Add credits
            </AppButton>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddCreditsForm;
